import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CHAT_TRANSFER_DEFAULT, CHAT_TRANSFER_TAGS, CHAT_WIDGET_DEFAULTS, chatSnippet, normalizeOrigin, type ChatEmbedInfo, type ChatKey, type ChatWidgetSettings, type CreatedChatKey } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';
import { CollapsibleCard } from '../components/CollapsibleCard';

const day = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const lines = (text: string) => text.split(/[\n,;]+/).map((t) => t.trim()).filter(Boolean);

/** Zum Kopieren: Schaltfläche mit Rückmeldung */
function CopyButton({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="btn btn--secondary btn--sm"
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 2000);
        } catch {
          setDone(false);
        }
      }}
    >
      {done ? 'Kopiert' : label}
    </button>
  );
}

const DAYS = [
  ['1', 'Mo'],
  ['2', 'Di'],
  ['3', 'Mi'],
  ['4', 'Do'],
  ['5', 'Fr'],
  ['6', 'Sa'],
  ['7', 'So'],
] as const;

/** Wochentage als Text („1-5“, „1,2,4“) in eine Auswahl. Leer heißt: jeden Tag. */
function parseDays(text: string): Set<string> {
  const out = new Set<string>();
  for (const part of text.split(',')) {
    const m = /^([1-7])-([1-7])$/.exec(part.trim());
    if (m) for (let i = Number(m[1]); i <= Number(m[2]); i++) out.add(String(i));
    else if (/^[1-7]$/.test(part.trim())) out.add(part.trim());
  }
  return out;
}
function formatDays(set: Set<string>): string {
  const list = DAYS.map(([k]) => k).filter((k) => set.has(k));
  return list.length === 0 || list.length === 7 ? '' : list.join(',');
}

type TextField = 'title' | 'greeting' | 'color' | 'privacyUrl' | 'supportEmail' | 'hours' | 'timezone' | 'transferText';

/** Einstellungen des Chat-Fensters, je Mandant gespeichert. Das Fenster auf der Webseite holt sie beim Laden, eine Änderung gilt sofort. */
function WidgetForm({ initial, canWrite }: { initial: ChatWidgetSettings; canWrite: boolean }) {
  const client = useQueryClient();
  const [v, setV] = useState<ChatWidgetSettings>(initial);
  const [days, setDays] = useState<Set<string>>(() => parseDays(initial.days));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const save = useMutation({
    mutationFn: async () => (await api<{ data: ChatWidgetSettings }>('PUT', '/webchat-embed/widget', { ...v, days: formatDays(days) })).data,
    onSuccess: (saved) => {
      setFields({});
      setMessage({ ok: true, text: 'Gespeichert. Das Chat-Fenster auf Ihrer Webseite übernimmt die Änderung beim nächsten Laden der Seite.' });
      client.setQueryData(['chat-widget'], saved);
    },
    onError: (e) => {
      const invalid = e instanceof ApiError && e.status === 422;
      setFields(invalid ? e.fields : {});
      setMessage({ ok: false, text: invalid ? 'Bitte prüfen Sie die markierten Felder.' : errorText(e, 'Das Speichern hat nicht geklappt.') });
    },
  });
  const text = (k: TextField) => ({
    value: v[k],
    onChange: (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value }),
    disabled: !canWrite,
    'aria-invalid': fields[k] ? true : undefined,
  });
  const err = (k: string) => (fields[k] ? <span className="field__error" role="alert">{fields[k]}</span> : null);
  const color = /^#[0-9a-fA-F]{6}$/.test(v.color) ? v.color : CHAT_WIDGET_DEFAULTS.color;

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setMessage(null);
        save.mutate();
      }}
      noValidate
    >
      <label className="check">
        <input type="checkbox" checked={v.enabled} disabled={!canWrite} onChange={(e) => setV({ ...v, enabled: e.target.checked })} />
        <span>Chat auf der Webseite anzeigen</span>
      </label>
      <p className="muted small">Ausgeschaltet verschwindet das Chat-Fenster von allen Ihren Webseiten, ohne dass Sie den Code entfernen. Laufende Gespräche bleiben in Wirkia.</p>

      <div className="form-grid">
        <label className="field">
          <span>Titel</span>
          <input className="input" maxLength={60} placeholder={CHAT_WIDGET_DEFAULTS.title} {...text('title')} />
          {err('title')}
        </label>
        <label className="field">
          <span>Farbe</span>
          <span className="chat-color">
            <input className="input" maxLength={7} placeholder={CHAT_WIDGET_DEFAULTS.color} {...text('color')} />
            <input type="color" aria-label="Farbe wählen" value={color} disabled={!canWrite} onChange={(e) => setV({ ...v, color: e.target.value })} />
          </span>
          {err('color')}
        </label>
        <label className="field">
          <span>Begrüßung</span>
          <textarea className="input" rows={3} maxLength={300} placeholder={CHAT_WIDGET_DEFAULTS.greeting} {...text('greeting')} />
          {err('greeting')}
        </label>
        <label className="field">
          <span>Datenschutzerklärung (Adresse)</span>
          <input className="input" placeholder="https://www.beispiel.de/datenschutz" {...text('privacyUrl')} />
          <span className="muted small">Das Fenster verweist darauf, bevor jemand schreibt. Mit https:// oder als Pfad wie /datenschutz.</span>
          {err('privacyUrl')}
        </label>
      </div>

      <h3>Weiterleitung an einen anderen Mitarbeiter</h3>
      <p className="muted small">
        Übernimmt ein anderer Mitarbeiter eine Unterhaltung (im Chat unter „Live Chats“ über die Zuweisung), bekommt der Besucher diesen Text als Nachricht. Ein erstmaliges Zuweisen löst ihn nicht aus. Leer heißt: Standardtext.
      </p>
      <label className="field">
        <span>Text der Weiterleitung</span>
        <textarea id="chat-transfer-text" className="input" rows={5} maxLength={600} placeholder={CHAT_TRANSFER_DEFAULT} {...text('transferText')} />
        {err('transferText')}
      </label>
      {canWrite && (
        <div className="form-actions" role="group" aria-label="Platzhalter einfügen">
          {CHAT_TRANSFER_TAGS.map((t) => (
            <button key={t.tag} type="button" className="btn btn--secondary btn--sm" title={t.label} onClick={() => setV((cur) => ({ ...cur, transferText: `${cur.transferText}${cur.transferText && !cur.transferText.endsWith(' ') ? ' ' : ''}${t.tag}` }))}>
              {t.tag}
            </button>
          ))}
        </div>
      )}
      <p className="muted small">Die Platzhalter setzt Wirkia beim Senden ein: {CHAT_TRANSFER_TAGS.map((t) => `${t.tag} = ${t.label.toLowerCase()}`).join(', ')}.</p>

      <h3>Supportzeiten</h3>
      <p className="muted small">Außerhalb der Zeiten bleibt der Chat offen, das Fenster sagt aber, dass die Antwort in der nächsten Schicht kommt. Ohne Angabe nennt das Fenster keine Zeiten.</p>
      <div className="form-grid">
        <label className="field">
          <span>Uhrzeiten</span>
          <input className="input" placeholder="08:00-22:00" {...text('hours')} />
          {err('hours')}
        </label>
        <label className="field">
          <span>Zeitzone</span>
          <input className="input" placeholder={CHAT_WIDGET_DEFAULTS.timezone} {...text('timezone')} />
          {err('timezone')}
        </label>
        <fieldset className="field">
          <legend>Wochentage</legend>
          <span className="checks">
            {DAYS.map(([k, label]) => (
              <label key={k} className="check">
                <input
                  type="checkbox"
                  checked={days.size === 0 || days.has(k)}
                  disabled={!canWrite}
                  onChange={(e) => {
                    const next = new Set(days.size === 0 ? DAYS.map(([x]) => x as string) : days);
                    if (e.target.checked) next.add(k);
                    else next.delete(k);
                    setDays(next);
                  }}
                />
                <span>{label}</span>
              </label>
            ))}
          </span>
          <span className="muted small">Ohne Haken und mit allen Haken gilt jeder Tag.</span>
        </fieldset>
        <label className="field">
          <span>E-Mail für Dringendes und für Anliegen ohne Antwort</span>
          <input className="input" type="email" placeholder="support@beispiel.de" {...text('supportEmail')} />
          <span className="muted small">Wird außerhalb der Supportzeiten im Fenster genannt. Antwortet nach 10 Minuten niemand im Chat, kann der Besucher sein Anliegen mit seiner E-Mail-Adresse an diese Adresse senden. Ohne Eintrag bietet das Fenster das nicht an.</span>
          {err('supportEmail')}
        </label>
      </div>

      <div className="chat-preview" aria-label="Vorschau" style={{ borderColor: color }}>
        <div className="chat-preview__head" style={{ background: color }}>{v.title.trim() || CHAT_WIDGET_DEFAULTS.title}</div>
        <div className="chat-preview__body">{v.greeting.trim() || CHAT_WIDGET_DEFAULTS.greeting}</div>
      </div>

      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      {canWrite && (
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Chat-Fenster speichern'}</button>
        </div>
      )}
    </form>
  );
}

/**
 * Einrichtung → APIs und Webhooks → Chat (Webseite): Der Mandant legt selbst Schlüssel für seine Webseite an, trägt ein, wo sie gelten, stellt das Chat-Fenster ein und bekommt den fertigen Code und die
 * Downloads (WordPress-Plugin, Paket für jede andere Webseite). Der ganze Schlüssel erscheint nur direkt nach dem Anlegen.
 */
export function ChatEmbedPage() {
  const client = useQueryClient();
  const { can } = useSession();
  const canWrite = can('setup.integrations');
  const info = useQuery({ queryKey: ['chat-embed'], queryFn: async () => (await api<{ data: ChatEmbedInfo }>('GET', '/webchat-embed')).data });
  const [name, setName] = useState('Webseite');
  const [origins, setOrigins] = useState('');
  const [created, setCreated] = useState<CreatedChatKey | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editOrigins, setEditOrigins] = useState('');
  const widget = useQuery({ queryKey: ['chat-widget'], queryFn: async () => (await api<{ data: ChatWidgetSettings }>('GET', '/webchat-embed/widget')).data });
  const [keyText, setKeyText] = useState('');

  const refresh = () => client.invalidateQueries({ queryKey: ['chat-embed'] });
  const create = useMutation({
    mutationFn: async () => (await api<{ data: CreatedChatKey }>('POST', '/webchat-embed/keys', { name, allowedOrigins: lines(origins) })).data,
    onSuccess: (k) => {
      setCreated(k);
      setKeyText(k.key);
      setMessage(null);
      refresh();
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Der Schlüssel konnte nicht angelegt werden.') }),
  });
  const update = useMutation({
    mutationFn: async (v: { id: string; allowedOrigins: string[] }) => (await api<{ data: ChatKey }>('PATCH', `/webchat-embed/keys/${v.id}`, { allowedOrigins: v.allowedOrigins })).data,
    onSuccess: () => {
      setEditing(null);
      setMessage({ ok: true, text: 'Die erlaubten Webseiten sind gespeichert.' });
      refresh();
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Speichern hat nicht geklappt.') }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/webchat-embed/keys/${id}`),
    onSuccess: () => {
      setMessage({ ok: true, text: 'Der Schlüssel ist gelöscht. Das Chat-Fenster auf Webseiten mit diesem Schlüssel kann keine Nachrichten mehr senden.' });
      refresh();
    },
    onError: (e) => setMessage({ ok: false, text: errorText(e, 'Das Löschen hat nicht geklappt.') }),
  });

  if (info.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!info.data) return <div className="alert alert--error" role="alert">Die Angaben konnten nicht geladen werden.</div>;
  const d = info.data;
  const active = d.keys.filter((k) => !k.revokedAt);
  const snippet = chatSnippet({ scriptUrl: d.scriptUrl, apiUrl: d.apiUrl, key: keyText.trim() || 'IHR-SCHLUESSEL' });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setCreated(null);
    setMessage(null);
    create.mutate();
  };

  return (
    <>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <CollapsibleCard title="Chat für Ihre Webseite" storageKey="chat-embed-intro" defaultOpen>
        <p className="muted small">
          Ihre Besucher schreiben im Chat-Fenster auf Ihrer Webseite, Sie antworten hier im Backoffice unter CRM-Abteilung → Chats. In vier Schritten ist es eingerichtet: Schlüssel anlegen, das Chat-Fenster einstellen, Code oder Plugin
          einbauen, prüfen. Wer die Webseite nicht selbst pflegt, gibt das Paket an seinen Webentwickler weiter.
        </p>
      </CollapsibleCard>

      <CollapsibleCard title="1. Schlüssel" storageKey="chat-embed-keys" defaultOpen>
        <p className="muted small">
          Der Schlüssel steht im Quelltext Ihrer Webseite. Er kann nur Chat-Nachrichten Ihrer Besucher entgegennehmen und sonst nichts. Zusätzlich gilt er nur auf den Webseiten, die Sie hier eintragen. Sie können bis zu {d.maxKeys} Schlüssel
          haben, zum Beispiel einen je Webseite.
        </p>

        {created && (
          <div className="alert alert--ok" role="status">
            <strong>Schlüssel angelegt.</strong> Kopieren Sie ihn jetzt. Danach zeigt Wirkia ihn nicht mehr, sondern nur seinen Anfang ({created.prefix}…). Geht er verloren, legen Sie einen neuen an.
            <div className="form-actions">
              <input className="input" readOnly value={created.key} aria-label="Schlüssel" onFocus={(e) => e.currentTarget.select()} />
              <CopyButton text={created.key} label="Schlüssel kopieren" />
            </div>
          </div>
        )}

        {d.keys.length > 0 && (
          <div className="table-scroll">
          <table className="table table--cards">
            <thead>
              <tr>
                <th>Name</th>
                <th>Schlüssel</th>
                <th>Erlaubte Webseiten</th>
                <th>Zuletzt benutzt</th>
                <th><span className="sr-only">Aktionen</span></th>
              </tr>
            </thead>
            <tbody>
              {d.keys.map((k) => (
                <tr key={k.id} className={k.revokedAt ? 'is-muted' : undefined}>
                  <td>{k.name}{k.revokedAt && <span className="badge badge--rejected"> Gesperrt</span>}</td>
                  <td data-label="Schlüssel" className="nowrap"><code>ktk_{k.prefix}…</code><div className="muted small">angelegt am {day.format(new Date(k.createdAt))}</div></td>
                  <td data-label="Erlaubte Webseiten">
                    {editing === k.id ? (
                      <>
                        <textarea className="input" rows={3} value={editOrigins} onChange={(e) => setEditOrigins(e.target.value)} placeholder="https://www.beispiel.de" aria-label="Erlaubte Webseiten, eine je Zeile" />
                        <div className="form-actions">
                          <button className="btn btn--primary btn--sm" type="button" disabled={update.isPending} onClick={() => update.mutate({ id: k.id, allowedOrigins: lines(editOrigins).map((o) => normalizeOrigin(o) ?? o) })}>Speichern</button>
                          <button className="btn btn--secondary btn--sm" type="button" onClick={() => setEditing(null)}>Abbrechen</button>
                        </div>
                      </>
                    ) : k.allowedOrigins.length > 0 ? (
                      k.allowedOrigins.map((o) => <div key={o}><code>{o}</code></div>)
                    ) : (
                      <span className="muted">Jede Webseite</span>
                    )}
                  </td>
                  <td data-label="Zuletzt benutzt">{k.lastUsedAt ? day.format(new Date(k.lastUsedAt)) : <span className="muted">noch nie</span>}</td>
                  <td className="cell-actions">
                    {canWrite && editing !== k.id && (
                      <div className="row-actions">
                        {!k.revokedAt && <button className="btn btn--secondary btn--sm" type="button" onClick={() => { setEditing(k.id); setEditOrigins(k.allowedOrigins.join('\n')); }}>Webseiten ändern</button>}
                        <button className="btn btn--ghost btn--sm" type="button" disabled={remove.isPending} onClick={() => { if (window.confirm(`Schlüssel „${k.name}“ löschen? Das Chat-Fenster auf Webseiten mit diesem Schlüssel kann danach nichts mehr senden. Das lässt sich nicht rückgängig machen.`)) remove.mutate(k.id); }}>Löschen</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {canWrite && active.length < d.maxKeys && (
          <form onSubmit={submit} noValidate>
            <h3>Neuen Schlüssel anlegen</h3>
            <div className="form-grid">
              <label className="field">
                <span>Name</span>
                <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="field">
                <span>Erlaubte Webseiten (eine je Zeile)</span>
                <textarea className="input" rows={3} value={origins} onChange={(e) => setOrigins(e.target.value)} placeholder={'https://www.beispiel.de\nhttps://beispiel.de'} />
                <span className="muted small">So, wie die Adresse im Browser steht, mit https:// und mit oder ohne www. Tragen Sie im Zweifel beide ein. Leer heißt: von jeder Webseite aus.</span>
              </label>
            </div>
            <div className="form-actions">
              <button className="btn btn--primary" type="submit" disabled={create.isPending || name.trim().length < 2}>{create.isPending ? 'Wird angelegt …' : 'Schlüssel anlegen'}</button>
            </div>
          </form>
        )}
      </CollapsibleCard>

      <CollapsibleCard title="2. Chat-Fenster" storageKey="chat-embed-window" defaultOpen>
        <p className="muted small">
          Titel, Farbe, Begrüßung und Supportzeiten stellen Sie hier ein, nicht im Code und nicht im Plugin. Das Fenster holt die Einstellungen beim Laden Ihrer Webseite von Wirkia. Leere Felder heißen: Standard.
        </p>
        {widget.isLoading ? <p className="muted">Wird geladen …</p> : widget.data ? <WidgetForm initial={widget.data} canWrite={canWrite} /> : <div className="alert alert--error" role="alert">Die Einstellungen konnten nicht geladen werden.</div>}
      </CollapsibleCard>

      <CollapsibleCard title="3. Code oder Plugin" storageKey="chat-embed-code" defaultOpen>
        <p className="muted small">Laden Sie das Plugin herunter oder kopieren Sie den fertigen Code in Ihre Webseite. Beides enthält dasselbe Chat-Fenster und bekommt seine Einstellungen von Wirkia.</p>
        <div className="form-actions">
          <a className="btn btn--primary" href={d.downloads.wordpress} download>WordPress-Plugin (ZIP)</a>
          <a className="btn btn--secondary" href={d.downloads.universal} download>Paket für jede andere Webseite (ZIP)</a>
          <a className="btn btn--ghost" href={d.downloads.guide} target="_blank" rel="noreferrer">Anleitung ansehen</a>
        </div>
        <p className="muted small">Version {d.version}. Im Paket liegt eine Anleitung für WordPress, Shopify, Wix, Joomla, TYPO3, Tag Manager und eigene Anwendungen.</p>

        <h3>Code für Ihre Webseite</h3>
        <label className="field">
          <span>Ihr Schlüssel (für den Code)</span>
          <input className="input" value={keyText} onChange={(e) => setKeyText(e.target.value)} placeholder="ktk_… (direkt nach dem Anlegen eingetragen)" autoComplete="off" spellCheck={false} />
          <span className="muted small">Wirkia kennt den Schlüssel nach dem Anlegen nicht mehr. Fügen Sie ihn hier ein, damit der Code vollständig ist, oder ersetzen Sie IHR-SCHLUESSEL später selbst.</span>
        </label>
        <pre className="code-block"><code>{snippet}</code></pre>
        <div className="form-actions">
          <CopyButton text={snippet} label="Code kopieren" />
        </div>
        <p className="muted small">Fügen Sie die Zeile auf allen Seiten kurz vor dem Ende ein (vor <code>&lt;/body&gt;</code>), am besten in die gemeinsame Vorlage. Beim Plugin und beim Paket liegt die Datei <code>wirkia-chat.js</code> bei, die Sie auf Ihrer Webseite ablegen und bei <code>src</code> eintragen. Wer sie nicht ablegen will, nimmt die Adresse oben im Code.</p>
      </CollapsibleCard>

      <CollapsibleCard title="4. Prüfen" storageKey="chat-embed-check" defaultOpen>
        <ol className="small">
          <li>Öffnen Sie Ihre Webseite in einem neuen Fenster. Unten rechts erscheint der Chat-Knopf.</li>
          <li>Schreiben Sie sich selbst eine Nachricht. Sie steht danach unter CRM-Abteilung → Chats, die Glocke oben zeigt neue Chats.</li>
          <li>Erscheint der Knopf nicht oder schlägt das Senden fehl, hilft der Abschnitt „Wenn etwas nicht klappt“ in der Anleitung. Meist fehlt die Adresse der Webseite beim Schlüssel, der Chat ist unter „Chat-Fenster“ ausgeschaltet oder ein Zwischenspeicher hält die alte Seite fest. Bei einem ungültigen Schlüssel und auf einer nicht freigegebenen Webseite zeigt sich das Fenster absichtlich nicht.</li>
        </ol>
        <p className="muted small">
          Datenschutz: Das Chat-Fenster setzt keine Cookies. Sobald ein Besucher schreibt, gehen Name, E-Mail (freiwillig) und Nachrichten an Wirkia, das sie in Ihrem Auftrag verarbeitet. Nennen Sie das in Ihrer Datenschutzerklärung.
        </p>
      </CollapsibleCard>
    </>
  );
}
